import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo, useState } from 'react'

import { TransactionTypeFilter } from '@features/bankTransactions/enums/transactionTypeFilter'
import { BookkeepingItemType } from '@features/reconciliation/enums/bookkeepingItemType'
import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { SortDirection } from '../../../../../enums/sortDirection'
import { BookkeepingItemSortProperty } from '../elements/BankTransactionDrawer/enums/bookkeepingItemSortProperty'
import { getTopMatches as getTopMatchesUtil } from '../elements/BankTransactionDrawer/utils/getTopMatches'
import { useSandboxBankAccountByBookkeepingAccountId } from '../hooks/useSandboxBankAccountByBookkeepingAccountId'
import { useSandboxBankTransactionsFilters } from '../hooks/useSandboxBankTransactionsFilters'
import { useSandboxBookkeepingItems } from '../hooks/useSandboxBookkeepingItems'
import { filterBookkeepingItems } from '../utils/filterBookkeepingItems'

export interface BookkeepingItemsFilters {
  searchQuery?: string
  sortProperty: BookkeepingItemSortProperty
  sortDirection: SortDirection
  types?: BookkeepingItemType[]
  transactionSideFilter?: TransactionTypeFilter
  minAmount?: number
  maxAmount?: number
}

interface BookkeepingItemsContextValue {
  filteredItems: BookkeepingItem[]
  totalItemsCount: number
  isLoading: boolean
  getTopMatches: (transaction: BankLine, count: number) => BookkeepingItem[]
  filters: BookkeepingItemsFilters
  setFilters: (updates: Partial<BookkeepingItemsFilters>) => void
  resetFilters: () => void
}

const BookkeepingItemsContext = createContext<BookkeepingItemsContextValue | undefined>(undefined)

const defaultFilters: BookkeepingItemsFilters = {
  searchQuery: undefined,
  sortProperty: BookkeepingItemSortProperty.Date,
  sortDirection: SortDirection.Desc,
  types: undefined,
  transactionSideFilter: undefined,
  minAmount: undefined,
  maxAmount: undefined,
}

interface BookkeepingItemsProviderProps {
  children: ReactNode
}

export const BookkeepingItemsProvider = ({ children }: BookkeepingItemsProviderProps): ReactElement => {
  const [bankTransactionsFilters] = useSandboxBankTransactionsFilters()
  const { currency: accountCurrency } = useSandboxBankAccountByBookkeepingAccountId(bankTransactionsFilters.accountId)

  const [filters, setFiltersState] = useState<BookkeepingItemsFilters>(defaultFilters)

  const { items: rawItems, isLoading } = useSandboxBookkeepingItems({
    accountId: bankTransactionsFilters.accountId,
  })

  const setFilters = useCallback((updates: Partial<BookkeepingItemsFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...updates }))
  }, [])

  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters)
  }, [])

  const items = useMemo(() => {
    return filterBookkeepingItems({
      items: rawItems,
      accountCurrency,
      searchQuery: filters.searchQuery,
      sortProperty: filters.sortProperty,
      sortDirection: filters.sortDirection,
      types: filters.types,
      transactionSideFilter: filters.transactionSideFilter,
      minAmount: filters.minAmount,
      maxAmount: filters.maxAmount,
    })
  }, [rawItems, filters, accountCurrency])

  const getTopMatches = useCallback(
    (transaction: BankLine, count: number): BookkeepingItem[] => {
      return getTopMatchesUtil({ transaction, items: rawItems, accountCurrency, count })
    },
    [rawItems, accountCurrency],
  )

  return (
    <BookkeepingItemsContext.Provider
      value={{
        filteredItems: items,
        totalItemsCount: rawItems.length,
        isLoading,
        getTopMatches,
        filters,
        setFilters,
        resetFilters,
      }}
    >
      {children}
    </BookkeepingItemsContext.Provider>
  )
}

export const useBookkeepingItemsContext = (): BookkeepingItemsContextValue => {
  const context = useContext(BookkeepingItemsContext)

  if (!context) {
    throw new Error('BookkeepingItemsContext is missing in the module!')
  }

  return context
}
